import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { MapContainer as LeafletMap, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import GoogleAddressAutocomplete from '../components/map/GoogleAddressAutocomplete';
import GoogleMap from '../components/map/GoogleMap';
import { googleMapsConfigured } from '../services/googleMaps';
import { PROPERTY_TYPE_GROUPS } from '../data/propertyTypes';
import PublishAlert from '../components/common/PublishAlert';

const TOURIST_PROPERTY_TYPES = [
  ['cabana', 'Cabaña'],
  ['casa', 'Casa'],
  ['departamento', 'Departamento'],
  ['habitacion', 'Habitación privada'],
  ['quinta', 'Quinta'],
  ['apart_hotel', 'Apart hotel'],
  ['hotel', 'Hotel'],
];

const locationIcon = L.divIcon({
  className: 'nido-location-host',
  html: '<div class="nido-location-pin"><i></i></div>',
  iconSize: [34, 44], iconAnchor: [17, 42],
});

function MapSelection({ onSelect }) {
  useMapEvents({ click: (event) => onSelect(event.latlng.lat, event.latlng.lng) });
  return null;
}

function RecenterMap({ center, hasLocation }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, hasLocation ? 15 : 11, { animate: true });
    window.setTimeout(() => map.invalidateSize(), 0);
  }, [map, center, hasLocation]);
  return null;
}


/* ================= CUSTOM SELECT ================= */

function CustomSelect({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = options.find((opt) => opt.value === value);

  return (
    <SelectWrapper ref={ref}>
      <SelectBox onClick={() => setOpen(!open)} active={open}>
        {selected ? selected.label : placeholder}
        <Arrow open={open}>▾</Arrow>
      </SelectBox>

      {open && (
        <Dropdown>
          {options.map((opt) => (
            <Option
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </Option>
          ))}
        </Dropdown>
      )}
    </SelectWrapper>
  );
}

/* ================= COMPONENTE PRINCIPAL ================= */

function NewProperty({ onSubmit, onBack, initialData = {} }) {

  const [touristStep, setTouristStep] = useState(0);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches);
  const [publishNotice, setPublishNotice] = useState('');
  const [addressSearching, setAddressSearching] = useState(false);


  const [form, setForm] = useState({
    title: "",
    price: "",
    showPriceReduction: true,
    currency: "USD",
    operation: "",
    type: "",
    province: "mendoza",
    department: "",
    neighborhood: "",
    address: "",
    lat: null,
    lng: null,
    placeId: "",
  });

  /* ===============================
     RELLENAR FORM SI VIENE DATA
     =============================== */

  useEffect(() => {

    if (!initialData || Object.keys(initialData).length === 0) return;

    setForm({
      title: initialData.title || "",
      price: initialData.price || "",
      showPriceReduction: initialData.showPriceReduction !== false,
      currency: initialData.currency || "USD",
      operation: initialData.operation || "",
      type: initialData.type || "",
      province: initialData.province || "mendoza",
      department: initialData.department || "",
      neighborhood: initialData.neighborhood || "",
      address: initialData.address || "",
      lat: initialData.location?.lat || null,
      lng: initialData.location?.lng || null,
      placeId: initialData.location?.placeId || "",
    });

  }, [initialData]);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 900px)');
    const update = () => setIsMobile(media.matches);
    update(); media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);


  const provinceCenter = useMemo(() => ({
    mendoza: [-32.8895, -68.8458],
    san_juan: [-31.5375, -68.5364],
    san_luis: [-33.3017, -66.3378],
  }[form.province] || [-32.8895, -68.8458]), [form.province]);
  const selectedMapCenter = useMemo(() => form.lat && form.lng ? [form.lat, form.lng] : provinceCenter, [form.lat, form.lng, provinceCenter]);
  const editableMapLocation = useMemo(
    () => form.lat && form.lng ? { lat: Number(form.lat), lng: Number(form.lng) } : null,
    [form.lat, form.lng]
  );

  const selectLocation = (lat, lng) => setForm((previous) => ({ ...previous, lat, lng }));

  /* ================= SUBMIT ================= */

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (!form.operation) {
      setPublishNotice('Seleccioná la operación.');
      return;
    }

    if (!form.type) {
      setPublishNotice('Seleccioná el tipo de propiedad.');
      return;
    }

    if (!form.title || !Number.isFinite(Number(form.price)) || Number(form.price) <= 0) {
      setPublishNotice('Completá el título y el precio real. No se permiten valores $0 ni “a consultar”.');
      return;
    }

    const incompleteLocation = form.operation === 'alquiler_vacacional'
      ? (!form.province || !form.address)
      : (!form.province || !form.department || !form.neighborhood || !form.address);
    if (incompleteLocation) {
      setPublishNotice('Completá provincia, departamento, barrio y dirección.');
      return;
    }

    const dataToSend = {
      ...form,
      location: {
        lat: form.lat,
        lng: form.lng,
        placeId:form.placeId,
      },
    };

    delete dataToSend.lat;
    delete dataToSend.lng;

    onSubmit(dataToSend);
  };

  /* ================= RENDER ================= */

  if (isMobile) {
    const isTourist = form.operation === 'alquiler_vacacional';
    const mobileTypes = isTourist ? TOURIST_PROPERTY_TYPES : PROPERTY_TYPE_GROUPS.flatMap((group) => group.options);
    const next = () => {
      if (touristStep === 0 && (!form.title || !form.type)) return setPublishNotice(`Completá el ${isTourist ? 'nombre del alojamiento' : 'título'} y el tipo de propiedad.`);
      if (touristStep === 1 && !form.address) return setPublishNotice(`Ingresá la dirección ${isTourist ? 'o el destino del alojamiento' : 'de la propiedad'}.`);
      if (touristStep < 2) return setTouristStep((step) => step + 1);
      handleSubmit();
    };
    const updateLocation = ({ address, lat, lng, placeId, province, department, neighborhood }) => setForm((previous) => ({ ...previous, address, lat, lng, placeId, province: province || previous.province, department: department || previous.department, neighborhood: neighborhood || previous.neighborhood }));
    return <TouristMobilePage>
      <TouristCard>
        {touristStep === 0 && <><small>{isTourist?'ALQUILER TURÍSTICO':form.operation==='venta'?'VENTA':'ALQUILER'}</small><h1>{isTourist?'Tu alojamiento.':'Tu propiedad.'}</h1><p>{isTourist?'Empecemos por cómo se va a presentar a los viajeros.':'Empecemos por cómo se va a presentar a las personas interesadas.'}</p><TouristField><Label>{isTourist?'Nombre del alojamiento':'Título de la publicación'}</Label><Input value={form.title} placeholder={isTourist?'Ej. Cabaña entre montañas':'Ej. Casa luminosa en Chacras'} onChange={(event) => setForm({ ...form, title: event.target.value })}/></TouristField><TouristField><Label>{isTourist?'Tipo de alojamiento':'Tipo de propiedad'}</Label><TouristTypeGrid>{mobileTypes.map(([value,label])=><button type="button" key={value} className={form.type===value?'selected':''} onClick={()=>setForm((previous)=>({...previous,type:value}))}>{label}</button>)}</TouristTypeGrid></TouristField></>}
        {touristStep === 1 && <><small>UBICACIÓN</small><h1>¿Dónde está?</h1><p>Elegí la dirección y confirmala en el mapa.</p><TouristField $addressField><Label>Dirección o destino</Label>{googleMapsConfigured ? <GoogleAddressAutocomplete value={form.address} onChange={(address) => setForm((previous) => ({ ...previous, address }))} onPlaceSelect={(place) => { setAddressSearching(false); updateLocation(place); }} onSearchingChange={setAddressSearching}/> : <Input value={form.address} placeholder="Dirección del alojamiento" onChange={(event) => setForm({ ...form, address:event.target.value })}/>}</TouristField>{!addressSearching && <>{!isTourist&&<><TouristField><Label>Departamento</Label><Input value={form.department} placeholder="Ej. Capital" onChange={(event)=>setForm((previous)=>({...previous,department:event.target.value}))}/></TouristField><TouristField><Label>Barrio o zona</Label><Input value={form.neighborhood} placeholder="Ej. Centro" onChange={(event)=>setForm((previous)=>({...previous,neighborhood:event.target.value}))}/></TouristField></>}<TouristMap>{googleMapsConfigured ? <GoogleMap editableLocation={editableMapLocation} onLocationChange={selectLocation}/> : <LeafletMap center={selectedMapCenter} zoom={form.lat && form.lng ? 15 : 11} scrollWheelZoom><TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" subdomains="abcd"/><RecenterMap center={selectedMapCenter} hasLocation={Boolean(form.lat && form.lng)}/><MapSelection onSelect={selectLocation}/>{form.lat && form.lng && <Marker position={[form.lat,form.lng]} icon={locationIcon}/>}</LeafletMap>}</TouristMap></>}</>}
        {touristStep === 2 && <><small>{isTourist?'VALOR DE LA ESTADÍA':form.operation==='venta'?'PRECIO DE VENTA':'VALOR DEL ALQUILER'}</small><h1>{isTourist?'Precio por noche.':form.operation==='venta'?'Precio de venta.':'Valor mensual.'}</h1><p>{isTourist?'Mostrá un valor claro. Nido no cobra comisión ni procesa reservas o señas.':'Mostrá el valor real de la propiedad para recibir consultas relevantes.'}</p><TouristPrice><PriceInput type="number" min="1" placeholder={isTourist?'Ej. 85000':'Ej. 120000'} value={form.price} onChange={(event) => setForm({ ...form, price:event.target.value })}/><TouristCurrencySelect aria-label="Moneda" value={form.currency} onChange={(event) => setForm((previous) => ({...previous,currency:event.target.value}))}><option value="USD">USD</option><option value="ARS">ARS</option></TouristCurrencySelect></TouristPrice><TouristPriceCaption>{isTourist?'Valor por noche':form.operation==='venta'?'Precio de venta':'Valor mensual'}</TouristPriceCaption><TouristHint>{isTourist?'Después definís capacidad, servicios y la experiencia de la estadía.':form.operation==='venta'?'Después sumás superficies, ambientes y datos de venta.':'Después definís condiciones, garantías y características.'}</TouristHint></>}
      </TouristCard>
      <TouristActions><button type="button" onClick={() => touristStep === 0 ? onBack() : setTouristStep((step) => step - 1)}>Atrás</button><button type="button" onClick={next}>{touristStep === 2 ? 'Continuar →' : 'Siguiente →'}</button></TouristActions>
      <PublishAlert message={publishNotice} onClose={() => setPublishNotice('')} />
    </TouristMobilePage>;
  }

  return (
    <Page>
      <PublishAlert message={publishNotice} onClose={() => setPublishNotice('')} />
      <Container>
        <Card>
          <Header>
            <IntentBadge>{form.operation === 'venta' ? 'VENTA' : form.operation === 'alquiler' ? 'ALQUILER' : form.operation === 'alquiler_temporal' ? 'ALQUILER TEMPORAL' : 'ESTADÍA VACACIONAL'}</IntentBadge>
            <Title>{form.operation === 'alquiler_vacacional' ? 'Diseñemos una estadía memorable' : 'Contanos sobre la propiedad'}</Title>
            <Subtitle>
              {form.operation === 'alquiler_vacacional' ? 'Ubicación, tipo de alojamiento y valor por noche. Después sumamos los detalles que enamoran.' : 'Empecemos por los datos esenciales y su ubicación.'}
            </Subtitle>
          </Header>

          <Form onSubmit={handleSubmit}>
            <Grid>

              <Field>
                <Label>Tipo de propiedad</Label>
                <CustomSelect
                  options={(form.operation === 'alquiler_vacacional'
                    ? TOURIST_PROPERTY_TYPES
                    : PROPERTY_TYPE_GROUPS.flatMap((group) => group.options)
                  ).map(([value, label]) => ({ value, label }))}
                  value={form.type}
                  onChange={(val) =>
                    setForm((prev) => ({ ...prev, type: val }))
                  }
                  placeholder="Seleccionar"
                />
              </Field>

              <Field>
                <Label>Provincia</Label>
                <CustomSelect
                  options={[
                    { value: "mendoza", label: "Mendoza" },
                    { value: "san_juan", label: "San Juan" },
                    { value: "san_luis", label: "San Luis" },
                  ]}
                  value={form.province}
                  onChange={(val) => setForm((prev) => ({ ...prev, province: val, department: "", neighborhood: "", address:"", lat:null, lng:null, placeId:"" }))}
                  placeholder="Seleccionar provincia"
                />
              </Field>

              <Field>
                <Label>Departamento</Label>
                <Input value={form.department} placeholder={form.operation === 'alquiler_vacacional' ? 'Ej. San Rafael' : 'Ej. Godoy Cruz'} onChange={(e) => setForm({ ...form, department: e.target.value })} />
              </Field>

              <Field>
                <Label>Barrio o zona</Label>
                <Input value={form.neighborhood} placeholder={form.operation === 'alquiler_vacacional' ? 'Ej. Valle Grande o Los Reyunos' : 'Ej. Chacras de Coria'} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} />
                <FieldHint>{form.operation === 'alquiler_vacacional' ? 'Usá el nombre del destino o zona turística para que los viajeros puedan encontrarlo.' : 'Revisalo antes de continuar: Google no siempre identifica barrios privados, distritos o nombres de uso local.'}</FieldHint>
              </Field>

              <Field>
                <Label>{form.operation === 'alquiler_vacacional' ? 'Nombre de tu alojamiento' : 'Título'}</Label>
                <Input
                  value={form.title}
                  onChange={(e) =>
                    setForm({ ...form, title: e.target.value })
                  }
                />
              </Field>

              <Field>
                <Label>{form.operation === 'alquiler_vacacional' ? 'Precio por noche' : ['alquiler','alquiler_temporal'].includes(form.operation) ? 'Precio mensual' : 'Precio de venta'}</Label>
                <PriceContainer>
                  <Currency>
                    {form.currency === "USD" ? "US$" : "$"}
                  </Currency>
                  <PriceInput
                    type="number"
                    min="1"
                    step="1"
                    value={form.price}
                    onChange={(e) =>
                      setForm({ ...form, price: e.target.value })
                    }
                  />
                  <CustomSelect
                    options={[
                      { value: "USD", label: "USD" },
                      { value: "ARS", label: "ARS" },
                    ]}
                    value={form.currency}
                    onChange={(val) =>
                      setForm((prev) => ({ ...prev, currency: val }))
                    }
                    placeholder="Moneda"
                  />
                </PriceContainer>
                <PriceRequirement>{form.operation === 'alquiler_vacacional' ? 'Informá el valor real por noche. NIDO no cobra comisión ni procesa reservas o señas.' : 'Informá el precio real ofrecido. NIDO no admite precio $0, valores simbólicos ni “consultar precio”.'}</PriceRequirement>
                {form.operation !== 'alquiler_vacacional' && initialData?._id && Number(form.price) > 0 && Number(form.price) < Number(initialData.price) && (
                  <ReductionChoice>
                    <input
                      type="checkbox"
                      checked={form.showPriceReduction !== false}
                      onChange={(event) => setForm((previous) => ({
                        ...previous,
                        showPriceReduction: event.target.checked,
                      }))}
                    />
                    <span>
                      <strong>Mostrar que bajó de precio</strong>
                      <small>La publicación llevará una señal visible con el porcentaje de la baja.</small>
                    </span>
                  </ReductionChoice>
                )}
              </Field>

              <AddressField full>
                <Label>Dirección exacta <small>(no se mostrará el número exacto en búsquedas)</small></Label>
                {googleMapsConfigured ? (
                  <GoogleAddressAutocomplete
                    value={form.address}
                    onChange={(address) => setForm((previous) => ({ ...previous, address }))}
                    onPlaceSelect={({ address, lat, lng, placeId, province, department, neighborhood }) => setForm((previous) => ({
                      ...previous,
                      address,
                      lat,
                      lng,
                      placeId,
                      province:province || previous.province,
                      department: department || previous.department,
                      neighborhood: neighborhood || previous.neighborhood,
                    }))}
                  />
                ) : <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />}
              </AddressField>

              <Field full>
                <MapHint>Hacé clic en el mapa o arrastrá el marcador para indicar la ubicación exacta.</MapHint>
                <MapWrapper>
                  {googleMapsConfigured ? <GoogleMap
                    editableLocation={editableMapLocation}
                    onLocationChange={selectLocation}
                  /> : <LeafletMap center={selectedMapCenter} zoom={form.lat && form.lng ? 15 : 11} scrollWheelZoom>
                    <TileLayer attribution='&copy; OpenStreetMap contributors &copy; CARTO' url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" subdomains="abcd" />
                    <RecenterMap center={selectedMapCenter} hasLocation={Boolean(form.lat && form.lng)} />
                    <MapSelection onSelect={selectLocation} />
                    {form.lat && form.lng && <Marker position={[form.lat, form.lng]} icon={locationIcon} draggable eventHandlers={{ dragend: (event) => { const point = event.target.getLatLng(); selectLocation(point.lat, point.lng); } }} />}
                  </LeafletMap>}
                </MapWrapper>
              </Field>

            </Grid>

            <ButtonRow>
              <Secondary type="button" onClick={onBack}>Atrás</Secondary>
              <Primary type="submit">
                Guardar y continuar
              </Primary>
            </ButtonRow>
          </Form>
        </Card>
      </Container>
    </Page>
  );
}

export default NewProperty;

/* ================= ESTILOS ================= */

const Page = styled.div`
  padding-top: 130px;
  background: radial-gradient(circle at 90% 0,rgba(198,111,78,.1),transparent 28%),#f7f5ef;
  min-height: 100vh;
  min-width: 0;
  overflow-x: hidden;

  @media (max-width: 600px) { padding-top: 52px; }
`;

const Container = styled.div`
  max-width: 1150px;
  margin: 0 auto;
  padding: 0 30px 100px;

  @media (max-width: 600px) { padding: 0 16px 72px; }
`;

const Card = styled.div`
  background: rgba(255,255,255,.78);
  border:1px solid rgba(41,71,79,.1);
  border-radius: 30px;
  padding: clamp(24px,5vw,54px);
  box-shadow: 0 25px 70px rgba(41,71,79,.08);

  @media (max-width: 600px) { border-radius: 22px; padding: 24px 18px; }
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  color:#29474f;font-size:clamp(34px,5vw,54px);line-height:1;letter-spacing:-.045em;font-weight:650;margin:8px 0 14px;
  overflow-wrap:anywhere;
`;

const Subtitle = styled.p`
  font-size: 17px;line-height:1.55;
  color: #68777b;max-width:680px;
`;
const IntentBadge=styled.span`color:#a95336;font-size:12px;font-weight:800;letter-spacing:.15em;`;

const Form = styled.form``;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 22px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  ${({ full }) => full && `grid-column: 1 / -1;`}
`;
const AddressField = styled(Field)`
  position:relative;
  z-index:25;
  overflow:visible;
`;

const Label = styled.label`
  color:#29474f;font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
`;
const FieldHint=styled.small`margin-top:7px;color:#718083;font-size:12px;line-height:1.45`;

const Input = styled.input`
  width:100%;min-width:0;box-sizing:border-box;
  height: 56px;padding:0 17px;font-size:16px;border-radius:16px;border:1px solid rgba(41,71,79,.15);background:#fff;

  &:focus {
    outline: none;
    border-color: #c66f4e;
    box-shadow: 0 0 0 3px rgba(198,111,78, 0.15);
  }
`;

const PriceContainer = styled.div`
  display: flex;
  gap: 10px;
  min-width:0;

  > * { min-width: 0; }
  > div:last-child { flex: 0 0 98px; }

  @media (max-width: 380px) {
    display:grid;
    grid-template-columns:auto minmax(0,1fr);
    > div:last-child { grid-column:1 / -1; }
  }
`;

const Currency = styled.div`
  display: flex;
  align-items: center;
  padding: 0 12px;
  background: #fafafa;
  border: 1px solid #e2e6ea;
  border-radius: 12px;
`;

const PriceInput = styled(Input)`
  flex: 1 1 0;
`;
const PriceRequirement=styled.p`margin:9px 0 0;color:#65777a;font-size:12px;line-height:1.5;`;
const ReductionChoice = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 11px;
  margin-top: 12px;
  padding: 13px 14px;
  border-radius: 13px;
  background: #f2f5f1;
  color: #29474f;
  cursor: pointer;

  input {
    width: 18px;
    height: 18px;
    margin-top: 2px;
    accent-color: #c66f4e;
  }

  strong, small {
    display: block;
  }

  strong {
    font-size: 13px;
  }

  small {
    margin-top: 3px;
    color: #718083;
    font-size:12px;
    line-height: 1.4;
  }
`;

const MapWrapper = styled.div`
  height: 300px;
  border-radius: 22px;
  overflow: hidden;
  border: 6px solid rgba(255,255,255,.92);
  box-shadow: 0 18px 46px rgba(42,64,70,.12), 0 0 0 1px rgba(42,64,70,.08);
  background: #ebe8df;

  .leaflet-container { width: 100%; height: 100%; font-family: inherit; }
  .leaflet-tile-pane { filter: saturate(.68) contrast(.96) sepia(.04); }
  .leaflet-control-zoom { overflow:hidden; border:0!important; border-radius:12px!important; box-shadow:0 10px 26px rgba(42,64,70,.13)!important; }
  .leaflet-control-zoom a { color:#29474f!important; background:rgba(252,251,248,.94)!important; border-color:rgba(42,64,70,.08)!important; }
  .leaflet-control-attribution { color: #667779; background: rgba(252,251,248,.9); font-size:12px; }
  .nido-location-host { background: transparent; border: 0; }
  .nido-location-pin { position: relative; width: 30px; height: 30px; background: #c66f4e; border: 4px solid #fff; border-radius: 50% 50% 50% 6px; box-shadow: 0 9px 24px rgba(42,64,70,.28); transform: rotate(-45deg); }
  .nido-location-pin i { position: absolute; inset: 7px; background: #fff; border-radius: 50%; }
`;

const MapHint = styled.p`margin:0 0 12px;color:#5f7073;font-size:15px;line-height:1.5;`;

const ButtonRow = styled.div`
  margin-top: 42px;
  display: flex;
  justify-content: space-between;align-items:center;gap:16px;
  @media(max-width:560px){align-items:stretch;flex-direction:column-reverse;margin-top:30px;button{width:100%;}}
`;
const Secondary=styled.button`border:0;background:transparent;color:#657679;font-size:15px;font-weight:650;cursor:pointer;padding:12px 0;`;

const Primary = styled.button`
  background: linear-gradient(135deg, #C66F4E 0%, #ff9d5a 100%);
  color: white;
  padding: 16px 44px;
  border-radius: 999px;
  border: none;
  font-weight: 600;
  font-size: 17px;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 12px 28px rgba(198,111,78, 0.35);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 38px rgba(198,111,78, 0.45);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 10px 20px rgba(198,111,78, 0.35);
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  min-width:0;
`;

const SelectBox = styled.div`
  height: 56px;
  padding: 0 17px;
  border-radius: 16px;
  border: 1px solid ${({ active }) => (active ? "#C66F4E" : "#e2e6ea")};
  background: #fff;font-size:16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;

  ${({ active }) =>
    active &&
    `
    box-shadow: 0 0 0 3px rgba(198,111,78,0.15);
  `}
`;

const Arrow = styled.span`
  transition: 0.2s;
  transform: ${({ open }) => (open ? "rotate(180deg)" : "rotate(0)")};
`;

const Dropdown = styled.div`
  position: absolute;
  top: 110%;
  width: 100%;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e6ea;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
  z-index: 50;
`;

const Option = styled.div`
  padding: 12px 14px;
  cursor: pointer;

  &:hover {
    background: rgba(198,111,78, 0.08);
  }
`;

const TouristMobilePage=styled.main`min-height:100dvh;padding:18px 16px 104px;background:radial-gradient(circle at 92% 0,rgba(198,111,78,.16),transparent 34%),#f7f5ef;color:#29474f;`;
const TouristCard=styled.section`position:relative;max-width:520px;margin:0 auto;padding:30px 20px 26px;overflow:visible;border:1px solid rgba(41,71,79,.1);border-radius:24px;background:linear-gradient(145deg,#fff 0%,#fffaf6 100%);box-shadow:0 18px 48px rgba(41,71,79,.08);&:before{content:'';position:absolute;top:-72px;right:-58px;width:180px;height:180px;border:1px solid rgba(198,111,78,.16);border-radius:50%;pointer-events:none}&:after{content:'✦';position:absolute;top:24px;right:29px;color:#c66f4e;font-size:25px;opacity:.55}> small,> h1,> p,> div{position:relative;z-index:1}>small{color:#b45f40;font-size:.68rem;font-weight:900;letter-spacing:.16em}h1{max-width:330px;margin:10px 0 13px;font-size:clamp(2rem,9vw,2.8rem);line-height:.98;letter-spacing:-.05em}p{margin:0 0 20px;color:#68797c;font-size:.95rem;line-height:1.55}`;
const TouristField=styled.div`position:relative;z-index:${({ $addressField }) => $addressField ? 100 : 1};display:grid;gap:7px;margin-top:17px;min-width:0;${Label}{font-size:.8rem}`;
const TouristTypeGrid=styled.div`display:grid;grid-template-columns:1fr 1fr;gap:8px;button{min-height:46px;padding:8px;border:1px solid #dce4e3;border-radius:12px;background:#fff;color:#29474f;font:inherit;font-size:.78rem;font-weight:750;cursor:pointer}button.selected{border-color:#c66f4e;background:#fff1e9;color:#b85f40;box-shadow:0 0 0 2px rgba(198,111,78,.12)}`;
const TouristMap=styled.div`position:relative;z-index:1!important;height:clamp(190px,34dvh,270px);margin-top:16px;overflow:hidden;border:1px solid #dce4e3;border-radius:17px;.leaflet-container{height:100%;width:100%}`;
const TouristPrice=styled.div`display:flex;gap:14px;align-items:center;margin-top:30px;${PriceInput}{min-width:0;height:58px;padding:0 17px;font-size:1.15rem}`;
const TouristCurrencySelect=styled.select`flex:0 0 108px;height:58px;padding:0 14px;border:1px solid #dce4e3;border-radius:16px;background:#fff;color:#29474f;font:inherit;font-size:1rem;font-weight:700;appearance:auto;&:focus{outline:none;border-color:#c66f4e;box-shadow:0 0 0 3px rgba(198,111,78,.12)}`;
const TouristPriceCaption=styled.span`display:block;margin:15px 0 0;color:#7a898b;font-size:.78rem;font-weight:700;`;
const TouristHint=styled.div`margin-top:14px;padding:19px 20px;border-radius:18px;background:#f4eee8;color:#5e7175;font-size:.88rem;line-height:1.5;`;
const TouristActions=styled.div`position:fixed;z-index:1100;right:0;bottom:0;left:0;display:grid;grid-template-columns:1fr 1.45fr;gap:10px;padding:13px 16px calc(13px + env(safe-area-inset-bottom));border-top:1px solid rgba(41,71,79,.1);background:rgba(255,253,249,.96);backdrop-filter:blur(14px);button{min-height:52px;border-radius:15px;font:inherit;font-size:.92rem;font-weight:850;cursor:pointer}button:first-child{border:1px solid #d5dfdc;background:#fff;color:#51696e}button:last-child{border:0;background:#c66f4e;color:#fff;box-shadow:0 9px 20px rgba(198,111,78,.2)}`;
